import bcrypt from "bcryptjs";
import { jwtDecode } from "jwt-decode";
import { showAlert } from "./Helpers";
import DOMPurify from 'dompurify';
import fetchDataWithAuth from "./api";
import { encryptUserId, decryptUserId } from "./cryptoUtils";

// Function to check if the token is expired
export const isTokenExpired = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Convert to seconds
    return decodedToken.exp < currentTime;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
};


/**
 * Returns the given data as the chart data.
 * @param {object} data The data to be used for the chart.
 * @returns {object} The chart data.
 */

export const getChartData = (parameters, parameterID, type) => {
  const data = { values: [] };
  if (parameters.length > 0) {
    parameters.forEach((item) => {
      if (item.parameterID === parameterID) {
        if (type === "surface") {
          data.values = item.historicValuesSurface;
        } else if (type === "bottom") {
          data.values = item.historicValuesBottom;
        }
        else {
          data.values = item.historicValuesSurface;
        }
      }
    });
  } else {
    data.values = [0, 0, 0, 0, 0]; // Default case if no parameters are present
  }
  return data;
}

export const generateChartData = (parameterID, type, newValue, parameters) => {
  const chartData = getChartData(parameters, parameterID, type) || { values: [] };
  let { values } = chartData;

  if (newValue !== undefined && newValue !== null && newValue !== "" && !isNaN(newValue)) {
    values = [newValue, ...values.slice(0, 6)]; // Insert newValue and ensure only the latest 7 values are kept
  }

  // Update chartData with modified values
  return { ...chartData, values };
};

export const fetchInputClassName = (value, rowIndex, parameters, showNotification = false, parameterID) => {
  if (value === "") return "input-bg-white";
  let currentParameter;
  if (typeof parameterID === "undefined") {
    currentParameter = parameters[rowIndex];
  }
  else {
    currentParameter = parameters.find((parameter) => parameter.parameterID === parameterID);
  }
  if (value !== null) {
    const { minValue, thresholdValue, maxValue, parameterName } = currentParameter;

    if (parameterName === "Dissolved Oxygen") {
      if (value < minValue || value > maxValue || value === 0) {
        if (showNotification) {
          showAlert(`${parameterName} input value ${value} is out of range (${minValue} - ${maxValue})`, "error",);
        }
        return "input-bg-red";
      }
      else {
        if (value >= minValue && value <= maxValue) {
          return "input-bg-green";
        }
      }
    }
    else {
      if ((value < minValue || value > maxValue)) {
        if (showNotification) {
          showAlert(`${parameterName} input value ${value} is out of range (${minValue} - ${maxValue})`, "error",);
        }
        return "input-bg-red";
      }
      else {
        if (thresholdValue == null && value >= minValue && value <= maxValue) {
          return "input-bg-green";
        } else if (thresholdValue != null && value >= minValue && value <= thresholdValue) {
          return "input-bg-green";
        } else if (thresholdValue != null && value >= thresholdValue && value <= maxValue) {
          if (showNotification) {
            showAlert(`${parameterName} input value ${value} is more than the threshold (${thresholdValue})`, "error",);
          }
          return "input-bg-orange";
        }
      }
    }
  }
  else {
    return "input-bg-white";
  }
}
export const validateParameters = (parameters, type, columnName) => {
  let allValid = true;
  parameters.forEach((parameter, rowIndex) => {
    if (parameter.parameterName !== "Sechi Disc Reading") {


      const inputElement = document.getElementById(
        `${type}${columnName}-${rowIndex}`
      );

      if (!inputElement.value) {
        inputElement.classList.add("input-bg-red1"); // Highlight invalid input in red
        allValid = false; // Mark as invalid
      } else {
        inputElement.classList.remove("input-bg-red1"); // Remove highlight if valid
      }
    }
  });
  return allValid
}

export const truncateString = (str, num) => {
  var modifiedstr = str.charAt(0).toUpperCase() + str.slice(1); //Capitalize the first word
  // var modifiedstr = str;
  if (str.length >= num) {
    modifiedstr = str.slice(0, num) + "...";
  }
  return (
    <span
      data-bs-toggle="tooltip"
      data-bs-placement="top"
      title={str}
      className="text-truncate"
      style={{ display: "inline-block" }}
    >
      {modifiedstr}
    </span>
  );
};
export const truncateLandingString = (str, num) => {

  if (str.length <= num) {
    // If the string doesn't need truncation, just return it
    return <div>{str}</div>;
  }

  // Truncate the string and add a tooltip for the rest
  const truncatedStr = str.slice(0, num);

  return (
    <div data-tooltip-id="DescTool"
      data-tooltip-content={str}
      data-scroll-hide="true"
      data-tooltip-place="top"
    >
      {truncatedStr}
      <span >
        {" "}
        ...
      </span>
    </div>
  );
};

export const getNameValidationMessage = (
  t,
  fieldValue,
  validationStringName,
  isRequired = true // Default value is true
) => {
  let validationMessage = "";
  // Check if the field is required and perform the required field validation
  if (isRequired) {
    if (fieldValue === null || fieldValue === undefined || fieldValue.toString().trim() === "") {
      validationMessage = t("requiredField");
      return validationMessage;
    }
  }

  return validationMessage;
};


export const getArabicNameValidationMessage = (
  t,
  fieldValue,
  validationStringName,
  isRequired = true // Default value is true
) => {
  // const nameRegex = /^[\u0600-\u06FFa-zA-Z0-9]+(?: [\u0600-\u06FFa-zA-Z0-9]+)*$/;
  let validationMessage = "";

  // Check if the field is required and perform the required field validation
  if (isRequired) {
    if (fieldValue === null || fieldValue === undefined || fieldValue.toString().trim() === "") {
      validationMessage = t("requiredField");
      return validationMessage;
    }
  }

  // Continue with the rest of the validation if the field is not empty
  /* if (fieldValue) {
     fieldValue = fieldValue.trim();
     // Check if the field matches the Arabic and English name validation regex
     if (!nameRegex.test(fieldValue)) {
       validationMessage = t(validationStringName);
     }
   }*/

  return validationMessage;
};


export const validatePhoneNumber = (
  t,
  value,
  isRequired = true // Default value is true
) => {
  const trimmedValue = value ? value.trim() : "";

  // Check if the field is required and perform the required field validation
  if (isRequired) {
    if (trimmedValue === "") {
      return t("requiredField"); // "This field is required";
    }
  }

  // Continue with the rest of the validations only if the value is provided
  if (trimmedValue) {
    // Check if the mobile number contains only numbers
    if (!/^\d+$/.test(trimmedValue)) {
      return t("phoneNumberValidationMessage"); // "Mobile number should contain only digits";
    }

    // Check if the mobile number is of the correct length (e.g., 10 digits)
    if (trimmedValue.length !== 10) {
      return t("phoneNumberLenValidationMessage"); // "Mobile number should be 10 digits long";
    }

    // Check if the mobile number is not all zeros
    if (/^0+$/.test(trimmedValue)) {
      return t("phoneNumberZeroValidationMessage"); // "Mobile number cannot be all zeros";
    }
  }

  // If no validation errors, return an empty string or undefined (valid case)
  return "";
};


export const statusLabel = (status, t) => {
  return status === "Active" || status === 1 ? (
    <span className="badge bg-success bgactivated">Active</span>
  ) : (
    <span className="badge bg-danger bgdeactivated">Inactive</span>
  );
};

export function validateEmail(t, email, isRequired = true) {
  // Trim the email value if it exists
  const trimmedEmail = email ? email.trim() : "";

  // Check if the field is required and perform the required field validation
  if (isRequired) {
    if (trimmedEmail === "") {
      return t("requiredField");; // Adjust the message as needed
    }
  }

  // Continue with the email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    return t("invalidEmail");
  }

  // If all validations pass, return valid
  return "";
}


export function getHeaders(token) {
  return {
    Authorization: "Bearer " + getLocalStorageItem("token"),
    "Accept-Language": getLanguage(),
  };
}

// export async function refreshAccessToken(user, token) {
//   try {
//     const response = await fetch(
//       `${process.env.REACT_APP_API_URL}/auth/reGenerateToken`,
//       {
//         method: "POST",
//         headers: {
//           'Authorization': `Bearer ${token}`,
//           'Accept': 'application/json',
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify(user),
//       }
//     );

//     if (response.ok) {
//       const responseJson = await response.json();
//       if (responseJson.token) {
//         localStorage.setItem('token', responseJson.token);
//         const expirationTime = new Date();
//         expirationTime.setMinutes(expirationTime.getMinutes() + responseJson.tokenExpirationTime);
//         localStorage.setItem('TokenExpTime', expirationTime);
//         setRefreshTimeout();
//       }
//     } else {
//       console.error('Failed to refresh token:', response.statusText);
//     }
//   } catch (error) {
//     console.error('Token refresh failed:', error);
//   }
// }


export async function refreshAccessToken(user, token) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/auth/reGenerateToken`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token
        })
        // headers: {
        //   'Authorization': `Bearer ${token}`,
        //   "Content-Type": "application/json",
        // }
      }
    );

    if (response.ok) {
      const responseJson = await response.json();
      if (responseJson.token) {
        localStorage.setItem('token', responseJson.token);
        const expirationTime = new Date();
        expirationTime.setMinutes(expirationTime.getMinutes() + responseJson.tokenExpirationTime);
        localStorage.setItem('TokenExpTime', expirationTime);
        setRefreshTimeout();
      }
    } else {
      console.error('Failed to refresh token:', response.statusText);
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }
}

export function startTokenRefresh() {
  const checkTokenExpiration = async () => {
    const tokenExpTime = localStorage.getItem('TokenExpTime');
    const token = localStorage.getItem('token');
    const user = {
      Email: localStorage.getItem("Email"),
      Id: localStorage.getItem("UserId"),
      DisplayName: localStorage.getItem("username")
    };

    if (!tokenExpTime) return;

    const expirationDate = new Date(tokenExpTime);
    const currentDate = new Date();

    if (currentDate >= expirationDate - 60000) { // 1 minute before expiration
      await refreshAccessToken(user, token);
    }
  };

  // Regular interval to check token refresh
  //const intervalId = setInterval(checkTokenExpiration, 3600000); // Every hour
  setRefreshTimeout();
  // Add event listener for visibility change
  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible") {
      //checkTokenExpiration(); // Check token when tab becomes active

      const token = localStorage.getItem('token');
      const user = {
        Email: localStorage.getItem("Email"),
        Id: localStorage.getItem("UserId"),
        DisplayName: localStorage.getItem("username")
      };

      await refreshAccessToken(user, token);
    }
  });

  //return () => clearInterval(intervalId); // Cleanup interval if needed
}

let tokenTimeout = null;
const setRefreshTimeout = () => {
  if (tokenTimeout) {
    clearTimeout(tokenTimeout);
  }

  const tokenExpTime = localStorage.getItem('TokenExpTime');
  if (!tokenExpTime) return;

  const expirationTime = new Date(tokenExpTime).getTime();
  const currentTime = new Date().getTime();

  const timeLeft = expirationTime - currentTime;
  const refreshIn = timeLeft - (3 * 60 * 1000);

  tokenTimeout = setTimeout(async () => {
    const token = localStorage.getItem("token");
    const user = {
      Email: localStorage.getItem("Email"),
      Id: localStorage.getItem("UserId"),
      DisplayName: localStorage.getItem("username"),
    };
    await refreshAccessToken(user, token);

  }, Math.max(refreshIn, 0));

  // if (refreshIn > 0) {
  //   let tokenTimeout = setTimeout(() => {
  //     const token = localStorage.getItem('token');
  //     const user = {
  //       Email: localStorage.getItem("Email"),
  //       Id: localStorage.getItem("UserId"),
  //       DisplayName: localStorage.getItem("username"),
  //     };
  //     refreshAccessToken(user, token)
  //   }, refreshIn);

  // }
  // else {
  //   const token = localStorage.getItem("token");
  //   const user = {
  //     Email: localStorage.getItem("Email"),
  //     Id: localStorage.getItem("UserId"),
  //     DisplayName: localStorage.getItem("username"),
  //   };
  //   refreshAccessToken(user, token);
  // }
};
////////////////

//Encrypt password
export async function handleEncrypt(password) {
  const saltRounds = 10; // Number of rounds determines the complexity
  const salt = await bcrypt.genSalt(saltRounds); // Generate a salt
  const encryptedPassword = await bcrypt.hash(password, salt); // Hash the password with the salt
  return encryptedPassword;
}

export function getLanguage() {
  return sessionStorage.getItem("i18nextLng") || "en";
}



// Format a date to a readable string
export function formatDate(date) {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(date).toLocaleDateString(undefined, options);
}

// Set an item in local storage
export function setLocalStorageItem(key, value) {
  localStorage.setItem(key, value);
}

// Get an item from local storage
export function getLocalStorageItem(key) {
  const value = localStorage.getItem(key) || sessionStorage.getItem(key);
  return value ? value : null;
}

// Remove an item from local storage
export function removeLocalStorageItem(key) {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

// Function to capitalize the first letter of a string
export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export async function logAuditEntry(userId, action, description, status, t, navigate) {
  try {
    let payload = {
      // userId,
      action,
      description,
      ipAddress: "", // Optional
      status,
    };

    payload.action = encryptUserId(action);
    payload.description = encryptUserId(description);
    payload.status = encryptUserId(status);

    // const response = await fetch(
    //   `${process.env.REACT_APP_API_URL}/AuditLog/log`,
    //   {
    //     method: "POST",
    //     headers: {
    //       "Content-Type": "application/json",
    //     },
    //     body: JSON.stringify(payload),
    //   }
    // );
    // if (!response.ok) {
    //   throw new Error(`HTTP error! Status: ${response.status}`);
    // }

    const response = await fetchDataWithAuth(
      `/AuditLog/log`,
      "POST",
      navigate,
      t,
      payload,
    );

    if (response.status !== "success") {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

  } catch (error) {
    if (error.name === "TypeError" && error.message === "Failed to fetch") {
      showAlert(t("networkError"), "error");
    } else {
      showAlert(error.message, "error");
    }
  }
}

export const generateDatetimeStamp = () => {
  const now = new Date().getTime();
  return now;
};

// Utility function to validate the URL
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};
export const validateFile = (
  file,
  validFileTypes,
  maxFileSize,
  setErrors,
  t,
  fileTypeErrorKey = "validimagefiles", // Default error key for file type validation
  fileSizeErrorKey = "imageSizeExceeded" // Default error key for file size validation
) => {
  const newErrors = {};

  // Check file type
  if (!validFileTypes.includes(file.type)) {
    newErrors.imageFile = t(fileTypeErrorKey); // Dynamic error message for invalid file type
    setErrors(newErrors); // Set the errors
    return false;
  }

  // Check file size
  if (file.size > maxFileSize) {
    newErrors.imageFile = t(fileSizeErrorKey, { maxSize: maxFileSize / 1024 / 1024 }); // Dynamic error message for file size exceeded, showing size in MB
    setErrors(newErrors); // Set the errors
    return false;
  }

  setErrors({}); // Clear any previous errors if validation passes
  return true;
};


// export const sanitizeInput = (input) => {
//   return input.replace(/[<>&'"]/g, function (char) {
//     switch (char) {
//       case '<': return '&lt;';
//       case '>': return '&gt;';
//       case '&': return '&amp;';
//       case '\'': return '&#39;';
//       case '"': return '&quot;';
//       default: return char;
//     }
//   });
// }

export const getUTMZone = (longitude) => {
  // Calculate UTM zone based on the longitude
  const zone = Math.floor((longitude + 180) / 6) + 1;
  return "UTM " + zone + "N";
}

export const roundToTwoDecimal = (value) => {
  return isNaN(value) ? 0 : Number(parseFloat(value).toFixed(2));
}

export const roundToOneDecimal = (value) => {
  return Number(parseFloat(value).toFixed(1));
};

export const roundToWholeNumber = (value) => {
  return Math.round(parseFloat(value));

};

export const customRoundToWholeNumber = (number) => {
  const decimalPart = +(number - Math.floor(number)).toFixed(2);
  if (decimalPart >= 0.51) {
    return Math.ceil(number);
  } else {
    return Math.floor(number);
  }
}

// reactSelectCustomStyles.js

export const getCustomStyles = (theme) => ({
  option: (provided, state) => ({
    ...provided,
    backgroundColor:
      theme === "dark" && (state.isFocused || state.isSelected)
        ? '#1967d2'
        : state.isFocused || state.isSelected
          ? '#1967d2'
          : null,
    color:
      theme === "light"
        ? state.isFocused || state.isSelected
          ? '#fff'
          : '#909090'
        : state.isFocused || state.isSelected
          ? '#fff'
          : '#fff',
    cursor: 'pointer',
  }),
  control: (provided) => ({
    ...provided,
    borderColor: '#ced4da', // Border color for the select input
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#b3c0cc', // Hover border color to match standard inputs
    },
    fontFamily: 'Omnes-Regular',
    minHeight: '32px',
    fontSize: '0.7875 rem'
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '4px', // Similar to default select
    overflow: 'hidden',
    fontFamily: 'Omnes-Regular',
    fontSize: '0.7875 rem'
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',  // This will hide the separator
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme === "dark" ? '#fff' : '#909090',
    //fontSize:  ,
    fontFamily: 'Omnes-Regular',
    fontSize: '0.7875 rem'
  }),
  input: (provided) => ({
    ...provided,
    color: theme === "dark" ? '#fff' : '#909090',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: theme === "dark" ? '#fff' : '#000',

  }),
  multiValue: (provided) => ({
    ...provided,
    fontSize: '0.9rem',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#000',
  }),
});

export const windDirectionToCompassDirection = (degrees) => {
  const directions = [
    "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
    "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"
  ];

  // Divide 360 degrees by 16 to get the number of degrees per direction
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}

// Sanitize Input Function
export const sanitizeInput = (input) => {
  if (typeof input !== 'string' || !input) return input;

  // Step 1: Remove any HTML tags (XSS protection)
  const sanitized = DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

  // Step 2: Remove URLs (http, https, www, or ftp links)
  const noUrls = sanitized.replace(/(http|https|ftp|www)\S+/gi, '');

  // Step 3: Remove dangerous protocols like javascript:, vbscript:, or data:
  const noDangerousProtocols = noUrls.replace(/\b(javascript|vbscript|data):[^\s]*\b/gi, '');

  // Step 4: Remove single quotes, semicolons, or other characters that could cause SQL injection
  const safeString = noDangerousProtocols.replace(/['";]/g, '');

  // Step 5: Trim whitespace
  return safeString.trim();
};


export const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

const userId = localStorage.getItem("UserId");
const webView = localStorage.getItem("webView");
const isWebView = webView === "true"; // Normalize the value: treat undefined, null, empty string, or "false" as false
if (userId && !isWebView) {
  startTokenRefresh(); /**applicable only for registered/logged in users */
}

// export async function generateToken() {
//   try {
//     const response = await fetch(
//       `${process.env.REACT_APP_API_URL}/Auth/token`,
//       {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json"
//         }
//       });

//     if (!response.ok) {
//       throw new Error("Failed to get ArcGIS token");
//     }

//     return await response.json();
//   } catch (err) {
//     console.error("Token Error:", err);
//     throw err;
//   }
// }




export async function GetToken() {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/Auth/GetMapServiceDetails`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });

    if (!response.ok) {
      throw new Error("Failed to get ArcGIS token");
    }

    const data = await response.json();
    if (data.token) {
      return data;
    } else {
      throw new Error(data.error?.message || 'Token generation failed');
    }
  } catch (err) {
    console.error("Token Error:", err);
    throw err;
  }
}


// export async function generateToken() {
//   const url = process.env.REACT_APP_TOKEN_URL;
//   const username = process.env.REACT_APP_TOKEN_USERID;
//   const password = process.env.REACT_APP_TOKEN_PASSWORD;
//   const referer = process.env.REACT_APP_TOKEN_REFERER;

//   if (!url || !username || !password) {
//     showAlert("token environment variables are not set", "error");
//     return null;
//   }

//   const params = new URLSearchParams({
//     client: 'referer',
//     referer: referer, //'https://edp.ead.ae/',
//     //expiration: '1440',
//     expiration: 1440,
//     username: username,
//     password: password,
//     f: 'json'
//   });

//   try {
//     const response = await fetch(url, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/x-www-form-urlencoded'
//       },
//       body: params
//     });

//     const data = await response.json();
//     if (data.token) {
//       return data;
//     } else {
//       throw new Error(data.error?.message || 'Token generation failed');
//     }
//   } catch (err) {
//     console.error("Token Error:", err);
//     throw err;
//   }
// }






// export async function generateToken(username, password) {
//   const url = window.tokenGenerationURL;  //"https://edp.ead.ae/portal/sharing/rest/generateToken";

//   const params = new URLSearchParams({
//     client: 'referer',
//     referer: window.tokenReferrer,  //'https://edp.ead.ae/',
//     expiration: '1440',
//     username: username,
//     password: password,
//     f: 'json'
//   });

//   try {
//     const response = await fetch(url, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/x-www-form-urlencoded'
//       },
//       body: params
//     });

//     const data = await response.json();
//     if (data.token) {
//       return data;
//     } else {
//       throw new Error(data.error?.message || 'Token generation failed');
//     }
//   } catch (err) {
//     console.error("Token Error:", err);
//     throw err;
//   }
// }




// export async function generateToken() {

//   const responseValues = await fetch(
//     `${process.env.REACT_APP_API_URL}/Auth/GetPortalDetails`,
//     {
//       method: "GET",
//       headers: {
//         "Content-Type": "application/json"
//       }
//     });

//   if (!responseValues.ok) {
//     throw new Error("Failed to get ArcGIS token");
//   }

//   const data = await responseValues.json();

//   const decryptUserName = decryptUserId(data.username);
//   const decryptPassword = decryptUserId(data.password);

//   const url = window.tokenGenerationURL;

//   const params = new URLSearchParams({
//     client: 'referer',
//     referer: window.tokenReferrer,  //'https://edp.ead.ae/',
//     expiration: '1440',
//     // username: username,
//     // password: password,
//     username: decryptUserName,
//     password: decryptPassword,
//     f: 'json'
//   });

//   try {
//     const response = await fetch(url, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/x-www-form-urlencoded'
//       },
//       body: params
//     });

//     const data = await response.json();
//     if (data.token) {
//       return data;
//     } else {
//       throw new Error(data.error?.message || 'Token generation failed');
//     }
//   } catch (err) {
//     console.error("Token Error:", err);
//     throw err;
//   }
// }